/**
 * Caricamento di un file su un obbligo, senza lasciare la pagina.
 *
 * Perche' una modale e non un rimando a «Documenti e cartelle»
 * -----------------------------------------------------------
 * Chi arriva qui sta gia' guardando il problema — un documento da correggere,
 * un'allerta su un obbligo scaduto — e sa gia' quale obbligo e quale file.
 * Mandarlo su un'altra pagina, a cercare di nuovo l'obbligo in un albero di 219
 * nodi, era il modo in cui «Sostituisci» e «Carica» non funzionavano.
 *
 * Il gesto resta quello di «Documenti e cartelle», con le stesse garanzie: il
 * file entra **in bozza**, passa dalla validazione del contenuto — un PDF da
 * scansione e' respinto, e l'errore compare qui dentro — e diventa pubblico
 * solo con «Pubblica». Il caricamento e la pubblicazione restano due gesti.
 *
 * Due usi
 * -------
 * * **Nuovo documento** su un obbligo: si sceglie il file e si scrive il titolo
 *   del link pubblico.
 * * **Sostituzione** (`titoloFisso`, `formatoAttuale`): titolo e formato non si
 *   scelgono. Sono quelli del documento sostituito, e sono cio' che alla
 *   pubblicazione sposta l'indirizzo «-latest» sul file nuovo: la famiglia e'
 *   per titolo e formato, e l'API respinge un sostituto in un altro formato.
 *   Qui lo si dice prima dell'invio.
 *
 * Con `conScadenza` la modale chiede anche la scadenza del file. Sulla
 * sostituzione serve sempre: la scadenza del vecchio non passa al sostituto, e
 * senza chiederla un impegno di rinnovo spariva senza che nessuno lo sapesse.
 * `scadenzaIniziale` la propone gia' compilata, quando quella del vecchio e'
 * ancora futura.
 *
 * Il trascinamento e' un'aggiunta: la zona di rilascio e' un pulsante, e da
 * tastiera o con uno screen reader si sceglie il file dalla finestra di sistema.
 *
 * Mentre il file viaggia la modale non si chiude. La richiesta non si puo'
 * ritirare a meta' — il server la completa comunque — e chiudere significava
 * vedere poi comparire un «caricato in bozza» per un gesto creduto annullato.
 */

import { useState } from "react";
import type { ReactNode } from "react";
import { ErroreApi } from "../api/client";
import type { CopiaCollegata, DataIt, Documento } from "../api/types";
import { sostituibiliInTutti, testoCopie } from "../lib/copie";
import { ESTENSIONI_AMMESSE, estensioneAmmessa, estensioneDi } from "../lib/documenti";
import { leggiScadenzaFile } from "../lib/scadenze";
import { CampoDato, CampoLibero } from "./campi";
import { Modale } from "./Modale";
import { Avviso, Bottone } from "./primitive";
import { FileScelto, ZonaRilascio } from "./ZonaRilascio";

export function ModaleCaricamento({
  titolo,
  contesto,
  fileIniziale = null,
  titoloFisso,
  formatoAttuale,
  copie = [],
  conScadenza = false,
  scadenzaIniziale = null,
  onInvia,
  onChiudi,
  onCompletato,
}: {
  /** Intestazione della modale. */
  titolo: string;
  /** Su che cosa si carica: tipicamente il percorso dell'obbligo. */
  contesto?: ReactNode;
  /** File gia' scelto, quando la modale si apre da un rilascio. */
  fileIniziale?: File | null;
  /** Titolo del link che non si puo' cambiare: e' il caso della sostituzione. */
  titoloFisso?: string;
  /** Formato del documento sostituito: il sostituto deve avere lo stesso. */
  formatoAttuale?: string;
  /**
   * Copie collegate del documento sostituito, negli altri obblighi. Se ci sono,
   * la modale offre di sostituire anche quelle — tutte o nessuna.
   */
  copie?: readonly CopiaCollegata[];
  /** Chiede anche la scadenza del file, facoltativa. */
  conScadenza?: boolean;
  /** Valore di partenza del campo della scadenza. */
  scadenzaIniziale?: DataIt | null;
  /** Esegue il caricamento; un `ErroreApi` si mostra dentro la modale. */
  onInvia: (
    file: File,
    titolo: string,
    opzioni: { ancheCopie: boolean; validoFinoAl: DataIt | null },
  ) => Promise<Documento>;
  onChiudi: () => void;
  /** `ancheCopie`: se il file e' stato sostituito anche nelle copie collegate. */
  onCompletato: (documento: Documento, opzioni: { ancheCopie: boolean }) => void;
}) {
  // Un file rilasciato su una riga non passa dalla zona di rilascio, che e'
  // dove si controlla l'estensione: il controllo si ripete qui, invece di
  // lasciare che sia il server a dire con un 422 cio' che si sapeva gia'.
  const inizialeAmmesso = fileIniziale !== null && estensioneAmmessa(fileIniziale.name);
  const [file, setFile] = useState<File | null>(inizialeAmmesso ? fileIniziale : null);
  const [titoloLink, setTitoloLink] = useState("");
  // Spuntata per difetto quando si puo': una copia rimasta col file vecchio e'
  // proprio cio' che le copie collegate esistono per evitare.
  const copieSostituibili = sostituibiliInTutti(copie);
  const [ancheCopie, setAncheCopie] = useState(copieSostituibili);
  const [scadenza, setScadenza] = useState(scadenzaIniziale ?? "");
  const scadenzaLetta = leggiScadenzaFile(scadenza);
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(
    fileIniziale && !inizialeAmmesso
      ? `Formato «${estensioneDi(fileIniziale.name) || "sconosciuto"}» non ammesso. Formati accettati: ${ESTENSIONI_AMMESSE.join(", ")}.`
      : null,
  );

  const titoloScelto = (titoloFisso ?? titoloLink).trim();
  const estensioneNuova = file ? estensioneDi(file.name) : "";
  const cambiaFormato =
    Boolean(formatoAttuale && estensioneNuova) &&
    estensioneNuova !== formatoAttuale?.toLowerCase();

  const chiudi = () => {
    if (!inCorso) onChiudi();
  };

  const invia = async () => {
    if (!file || !titoloScelto || cambiaFormato || scadenzaLetta.errore) return;
    setErrore(null);
    setInCorso(true);
    try {
      const opzioni = { ancheCopie: copieSostituibili && ancheCopie };
      const documento = await onInvia(file, titoloScelto, {
        ...opzioni,
        validoFinoAl: scadenzaLetta.valore,
      });
      onCompletato(documento, opzioni);
    } catch (causa) {
      setErrore(
        causa instanceof ErroreApi ? causa.messaggioEsteso : "Caricamento non riuscito.",
      );
    } finally {
      setInCorso(false);
    }
  };

  return (
    <Modale
      titolo={titolo}
      aperta
      onChiudi={chiudi}
      pedice={
        <>
          <Bottone variante="fantasma" onClick={chiudi} disabled={inCorso}>
            Annulla
          </Bottone>
          <Bottone
            variante="primario"
            inCorso={inCorso}
            disabled={!file || !titoloScelto || cambiaFormato || Boolean(scadenzaLetta.errore)}
            onClick={() => void invia()}
          >
            Carica in bozza
          </Bottone>
        </>
      }
    >
      <div className="pila">
        {contesto ? <div>{contesto}</div> : null}

        {errore ? (
          <Avviso tono="critico" titolo="Caricamento non riuscito">
            {errore}
          </Avviso>
        ) : null}

        {titoloFisso !== undefined ? (
          <div>
            <p className="anteprima__etichetta">Titolo del link pubblico</p>
            <p>{titoloFisso}</p>
            <p className="campo__aiuto">
              Resta quello del documento sostituito: e' cio' che lega il file nuovo al
              vecchio, e alla pubblicazione sposta su di esso l'indirizzo «-latest». Il
              vecchio resta raggiungibile al suo indirizzo.
            </p>
          </div>
        ) : null}

        <ZonaRilascio
          onFile={(scelto) => {
            setFile(scelto);
            setErrore(null);
          }}
          disabilitata={inCorso}
        />
        {file ? <FileScelto file={file} onRimuovi={() => setFile(null)} /> : null}

        {cambiaFormato ? (
          <Avviso tono="critico" titolo="Serve lo stesso formato">
            Il documento da sostituire e' in {formatoAttuale?.toUpperCase()}, il file scelto in{" "}
            {estensioneNuova.toUpperCase()}. Il sostituto deve restare nello stesso formato: e'
            cio' che gli fa ereditare l'indirizzo «-latest». Un documento in un altro formato si
            carica come documento nuovo, da Documenti e cartelle.
          </Avviso>
        ) : null}

        {copie.length > 0 ? (
          <div>
            <label className="casella">
              <input
                type="checkbox"
                checked={copieSostituibili && ancheCopie}
                disabled={!copieSostituibili || inCorso}
                onChange={(evento) => setAncheCopie(evento.target.checked)}
              />
              <span>Sostituisci anche nelle copie collegate: {testoCopie(copie)}</span>
            </label>
            <p className="campo__aiuto">
              {copieSostituibili
                ? "Lo stesso file corretto va in bozza anche negli altri obblighi, ciascuno con il proprio indirizzo."
                : "Una copia e' archiviata e non si sostituisce: si sostituisce solo questo documento."}
            </p>
          </div>
        ) : null}

        {titoloFisso === undefined ? (
          <CampoLibero
            etichetta="Titolo del link pubblico"
            valore={titoloLink}
            onCambia={setTitoloLink}
            obbligatorio
            aiuto="E' il testo che legge chi naviga con uno screen reader: deve descrivere il contenuto, non il file."
          />
        ) : null}

        {conScadenza ? (
          <CampoDato
            etichetta="Scadenza del file (facoltativa)"
            tipo="data"
            valore={scadenza}
            onCambia={setScadenza}
            errore={scadenza.trim() ? scadenzaLetta.errore : null}
            aiuto="Entro questa data va caricato un file aggiornato; passata, il file resta pubblicato."
          />
        ) : null}

        <p className="campo__aiuto">
          Il file entra in bozza e non e' ancora sul portale: diventa pubblico con
          «Pubblica», nel riquadro «Da pubblicare» di Documenti e cartelle.
        </p>
      </div>
    </Modale>
  );
}
